import { api } from './apiClient';
import { jwtDecode } from 'jwt-decode';
import type {
  User,
  LoginCredentials,
  SignupCredentials,
  ResetPasswordPayload,
} from '@/store/slices/authSlice';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

interface TokenPayload {
  sub: string;
  email: string;
  name: string;
  role: 'admin' | 'employer' | 'candidate';
  organizationUid?: string;
  organizationName?: string;
  exp: number;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.interviewAgent.post<{
      accessToken: string;
      refreshToken: string;
    }>('/auth/login', credentials);

    const decoded = jwtDecode<TokenPayload>(response.accessToken);

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      user: {
        uid: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        role: decoded.role,
        organizationUid: decoded.organizationUid,
        organizationName: decoded.organizationName,
      },
    };
  },

  async signup(credentials: SignupCredentials): Promise<AuthResponse> {
    const response = await api.interviewAgent.post<{
      accessToken: string;
      refreshToken: string;
    }>('/auth/signup', credentials);

    const decoded = jwtDecode<TokenPayload>(response.accessToken);

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      user: {
        uid: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        role: decoded.role,
        organizationUid: decoded.organizationUid,
        organizationName: decoded.organizationName,
      },
    };
  },

  async resetPassword(
    payload: ResetPasswordPayload
  ): Promise<{ message: string }> {
    return api.interviewAgent.post('/auth/reset-password', payload);
  },

  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    return api.interviewAgent.post('/auth/refresh-token', { refreshToken });
  },

  async verifyToken(token: string): Promise<User> {
    const decoded = jwtDecode<TokenPayload>(token);

    // Check if token is expired
    if (decoded.exp * 1000 < Date.now()) {
      throw new Error('Token expired');
    }

    return {
      uid: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      role: decoded.role,
      organizationUid: decoded.organizationUid,
      organizationName: decoded.organizationName,
    };
  },

  async changePassword(payload: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> {
    return api.interviewAgent.post('/auth/change-password', payload);
  },

  async updateProfile(payload: Partial<User>): Promise<User> {
    return api.interviewAgent.put('/auth/profile', payload);
  },
};
